/**
 * BrandContext — white label (EMB-16)
 *
 * One context, two values: the default IS today's hardcoded TouchWorks
 * literals, so a consumer that reads `useBrand()` renders byte-identically
 * to today whenever no `BrandProvider` is mounted above it (the entire
 * non-embed `/internal/*` tree). Only the `/embed/*` tree wraps its content
 * in `BrandProvider`, which fetches the parent account's configured
 * name/logo (`GET /api/embed/brand`, T19 backend) once authenticated and
 * replaces the default — one code path, two values, exactly like the
 * pattern context.md's white-label decision calls for.
 *
 * Palette/theme are deliberately untouched — this context only ever carries
 * a name and a logo URL.
 */
import { createContext, useContext, useEffect, useState } from "react";
import { useEmbed } from "@/contexts/EmbedContext";
import { getEmbedAuthHeaders } from "@/lib/embedSession";

export const DEFAULT_BRAND = Object.freeze({ displayName: "TouchWorks", logoUrl: null });

const BrandContext = createContext(DEFAULT_BRAND);

export function BrandProvider({ children }) {
  const { isEmbed } = useEmbed();
  const [brand, setBrand] = useState(DEFAULT_BRAND);

  useEffect(() => {
    if (!isEmbed) {
      setBrand(DEFAULT_BRAND);
      return undefined;
    }

    let cancelled = false;

    fetch("/api/embed/brand", { credentials: "include", headers: getEmbedAuthHeaders() })
      .then((res) => (res.ok ? res.json() : { brand: null }))
      .then((data) => {
        if (cancelled) return;
        // Sem config de parceiro (ou a leitura falhou) → mantém o padrão
        // TouchWorks; nunca deixa a tela sem nome/logo nenhum.
        if (data?.brand?.displayName) {
          setBrand({ displayName: data.brand.displayName, logoUrl: data.brand.logoUrl ?? null });
        }
      })
      .catch(() => {}); // falha de rede → mantém o padrão, não trava o shell

    return () => {
      cancelled = true;
    };
  }, [isEmbed]);

  return <BrandContext.Provider value={brand}>{children}</BrandContext.Provider>;
}

export function useBrand() {
  return useContext(BrandContext);
}
